import React from 'react';
import { IoEllipseSharp } from 'react-icons/io5';
import PropTypes from 'prop-types';
import useModal from './useModal';
import useOrganizationSearch from './useOrganizationSearch';
import HighlightText from './HighlightText';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import Routes from 'utils/routes.js.erb';
import { authHeaders } from 'actions';
import { errorMessage, customMessage } from 'actions/internal';
import { connect } from 'react-redux';
import i18n from 'utils/i18n';
import { useTranslation } from 'react-i18next';
import Tooltip from 'rc-tooltip';
import { downloadPDF } from 'actions/download';
import { DropdownButton, DropdownItem } from 'components/ui/DropdownButton';
import localeNames from 'utils/localeNames.jsx.erb';

const CohortItem = ({group, subgroup, cohort, onCustomMessage, onErrorMessage}) => {
  const { t } = useTranslation();
  const { openModal } = useModal();
  const { results, activeQuery } = useOrganizationSearch();
  const queryClient = useQueryClient();
  const isMatch = !!(results && results.matchedCohortIds.has(cohort.id));

  const remove = useMutation({
    mutationFn: (cohort) => {
      return axios.delete(Routes.api_dashboard_administration_organizations_destroy_cohort_path({ cohort_id: cohort.id }), { headers: authHeaders() })
    },
    onSuccess: () => {
      onCustomMessage(i18n.t('administration.cohortItem.deleted'), 'success');
      queryClient.invalidateQueries({ queryKey: ['administration', 'organization', 'groups', group.id, 'subgroups', subgroup.id, 'cohorts'] })
    },
    onError: (err) => {
      if (axios.isAxiosError(err)) {
        onErrorMessage(Object.values(err.response.data.errors).flat().join(', '));
      } else {
        onErrorMessage(i18n.t('errors.generic'));
      }
    },
  })

  return (
    <div className={`row-cell${isMatch ? ' row-cell--matched' : ''}`}>
      <span className='cell expand level3'>
        <IoEllipseSharp style={{color: 'blue'}} />
      </span>
      <span className='cell id'>
        {cohort.id}
      </span>
      <span className='cell name grow'>
        <HighlightText text={cohort.name} query={activeQuery} />
        {!cohort.is_na && (
          <>
            {cohort.can_edit && (
              <a className="edit" onClick={() => openModal('cohort', group, subgroup, cohort)}>
                <i className="fa fa-pencil"></i>
                &nbsp;
                {t('actions.edit')}
              </a>
            )}
            {cohort.can_delete && (
              <a className="remove" onClick={() => window.confirm(i18n.t('confirm.deleteCohort')) && remove.mutate(cohort)}>
                <i className="fa fa-trash"></i>
                &nbsp;
                {t('actions.remove')}
              </a>
            )}
          </>
        )}
      </span>
      <span className='cell end code'>
        <div className='u-flex u-flex-space-between'>
          <Tooltip placement="top" overlay={<span>{t('administration.cohortItem.cohortCode')}</span>}>
            <div className='truncate'>{cohort.code}</div>
          </Tooltip>
          { cohort.code?.toString().length > 0 && (
            <div style={{ marginTop: -3 }}>
              <DropdownButton label={<i className="fa fa-file-pdf-o" />}>
                {Object.keys(localeNames).map((locale) => (
                  <DropdownItem
                    key={locale}
                    onClick={() =>
                      downloadPDF(
                        Routes.api_dashboard_administration_organizations_cohort_pdf_path({ cohort_id: cohort.id, format: '_pdf', locale }),
                        `${cohort.name}-${cohort.code}-${locale}.pdf`
                      )
                    }
                  >
                    {t(`localeNames.${locale}`)}
                  </DropdownItem>
                ))}
              </DropdownButton>
            </div>
          )}
        </div>
      </span>
      <span className='cell end users'>
        <Tooltip placement="top" overlay={<span>{t('administration.cohortItem.numberOfChildren')}</span>}>
          <div>
            <i className="fa fa-users"></i>
            {cohort.players_count}
          </div>
        </Tooltip>
      </span>
    </div>
  );
}

CohortItem.propTypes = {
  group: PropTypes.object.isRequired,
  subgroup: PropTypes.object.isRequired,
  cohort: PropTypes.object.isRequired,
  onErrorMessage: PropTypes.func.isRequired,
  onCustomMessage: PropTypes.func.isRequired,
}

const mapStateToProps = () => {
  return {}
}

const mapDispatchToProps = (dispatch) => {
  return {
    onErrorMessage: (message) => {
      dispatch(errorMessage(message))
    },
    onCustomMessage: (message, type) => {
      dispatch(customMessage(message, type))
    },
  }
};

export default connect(mapStateToProps, mapDispatchToProps)(CohortItem);
